import {
  buildPathFromAnchors,
  buildPathFromSegments,
  parseLinearAnchors,
  parsePathSegments,
} from '../lib/interaction/path';
import { TYPES } from '../render/constants';
import { deserializePoints, serializePoints } from '../render/line';
import { Action } from './action';
import { removeOnlyOneChildGroup, updateGroupSize } from './group';

export function deleteActionHandler(stageContext) {
  const selectedNodes = stageContext.api.getSelectedNodes();
  if (handleDeleteKey(stageContext)) return;
  if (handlePathDeleteKey(stageContext)) return;

  selectedNodes.forEach((node) => {
    stageContext.api.removeNode(node.id);
  });
  const groups = updateGroupSize(
    stageContext,
    selectedNodes.map((node) => {
      return {
        node,
      };
    })
  );
  const hasFrame = selectedNodes.some((node) => node.type === TYPES.frame);
  removeOnlyOneChildGroup(stageContext, groups);
  stageContext.api.clearSelect();
  if (hasFrame) {
    stageContext.api.layoutFrame();
  }
}

export const deleteAction = new Action({
  name: 'Delete',
  handler: deleteActionHandler,
  shortcuts: ['Backspace', 'Delete'],
});

// 处理Delete键删除Path节点控制点
function handlePathDeleteKey(stageContext) {
  const selectedNodes = stageContext.api.getSelectedNodes();
  if (selectedNodes.length !== 1) return;

  const node = selectedNodes[0];
  if (node.type !== TYPES.path) return;

  // 找到当前选中的控制点
  const interactive = stageContext.providers.interactive;
  if (!interactive.hoverHandlerId || !interactive.hoverHandlerId.startsWith('pathPoint_')) {
    return;
  }

  const pointIndex = parseInt(interactive.hoverHandlerId.split('_')[1]);
  if (isNaN(pointIndex) || pointIndex < 0) {
    return;
  }

  // 尝试解析复杂路径（支持曲线）
  const segmentsParsed = parsePathSegments(node.path);
  if (segmentsParsed) {
    const { anchors, segments, closed } = segmentsParsed;
    if (!anchors || pointIndex >= anchors.length || anchors.length <= 2) {
      return;
    }

    // 创建新的锚点数组，删除指定索引的点
    const newAnchors = anchors.filter((_, idx) => idx !== pointIndex);

    // 更新segments中的索引引用
    const newSegments = segments
      .filter((seg) => seg.from !== pointIndex && seg.to !== pointIndex)
      .map((seg) => ({
        ...seg,
        from: seg.from > pointIndex ? seg.from - 1 : seg.from,
        to: seg.to > pointIndex ? seg.to - 1 : seg.to,
      }));

    const newPath = buildPathFromSegments(newAnchors, newSegments, closed);
    stageContext.api.updateNode(node.id, { path: newPath });
  } else {
    // 回退到线性路径处理
    const parsed = parseLinearAnchors(node.path);
    if (!parsed) return;

    const { anchors, closed } = parsed;
    if (!anchors || pointIndex >= anchors.length || anchors.length <= 2) {
      return;
    }

    // 删除指定索引的点
    const newAnchors = anchors.filter((_, idx) => idx !== pointIndex);
    const newPath = buildPathFromAnchors(newAnchors, closed);
    stageContext.api.updateNode(node.id, { path: newPath });
  }

  // 清除hover状态
  interactive.hoverHandlerId = '';

  stageContext.hooks.onNodeResizeCallback();
  return true;
}

// 新增：删除Path节点控制点的便捷函数
export function deletePathPoint(stageContext, nodeId, pointIndex) {
  const node = stageContext.api.getNode(nodeId);
  if (!node || node.type !== TYPES.path) return false;

  const segmentsParsed = parsePathSegments(node.path);
  if (segmentsParsed) {
    const { anchors, segments, closed } = segmentsParsed;
    if (!anchors || pointIndex >= anchors.length || anchors.length <= 2) {
      return false;
    }

    const newAnchors = anchors.filter((_, idx) => idx !== pointIndex);
    const newSegments = segments
      .filter((seg) => seg.from !== pointIndex && seg.to !== pointIndex)
      .map((seg) => ({
        ...seg,
        from: seg.from > pointIndex ? seg.from - 1 : seg.from,
        to: seg.to > pointIndex ? seg.to - 1 : seg.to,
      }));

    const newPath = buildPathFromSegments(newAnchors, newSegments, closed);
    stageContext.api.updateNode(nodeId, { path: newPath });
    return true;
  } else {
    const parsed = parseLinearAnchors(node.path);
    if (!parsed) return false;

    const { anchors, closed } = parsed;
    if (!anchors || pointIndex >= anchors.length || anchors.length <= 2) {
      return false;
    }

    const newAnchors = anchors.filter((_, idx) => idx !== pointIndex);
    const newPath = buildPathFromAnchors(newAnchors, closed);
    stageContext.api.updateNode(nodeId, { path: newPath });
    return true;
  }
}

// 处理Delete键删除Line节点控制点
function handleDeleteKey(stageContext) {
  const selectedNodes = stageContext.api.getSelectedNodes();
  if (selectedNodes.length !== 1) return;

  const node = selectedNodes[0];
  if (node.type !== TYPES.line) return;

  const points = deserializePoints(node.points);
  if (!points || points.length <= 2) {
    // 如果只有2个点或更少，不允许删除
    return;
  }

  // 找到当前选中的控制点
  const interactive = stageContext.providers.interactive;
  if (!interactive.hoverHandlerId || !interactive.hoverHandlerId.startsWith('linePoint_')) {
    return;
  }

  const pointIndex = parseInt(interactive.hoverHandlerId.split('_')[1]);
  if (isNaN(pointIndex) || pointIndex < 0 || pointIndex >= points.length) {
    return;
  }

  // 删除指定索引的点
  const newPoints = [...points];
  newPoints.splice(pointIndex, 1);

  // 更新节点
  stageContext.api.updateNode(node.id, {
    points: serializePoints(newPoints),
  });

  // 清除hover状态
  interactive.hoverHandlerId = '';

  stageContext.hooks.onNodeResizeCallback();
  return true;
}

// 新增：删除Line节点控制点的便捷函数
export function deleteLinePoint(stageContext, nodeId, pointIndex) {
  const node = stageContext.api.getNode(nodeId);
  if (!node || node.type !== TYPES.line) return false;

  const points = deserializePoints(node.points);
  if (!points || points.length <= 2) return false;

  if (pointIndex < 0 || pointIndex >= points.length) return false;

  // 删除指定索引的点
  const newPoints = [...points];
  newPoints.splice(pointIndex, 1);

  // 更新节点
  stageContext.api.updateNode(node.id, {
    points: serializePoints(newPoints),
  });

  stageContext.hooks.onNodeResizeCallback();
  return true;
}

// 新增：在Line节点指定位置添加控制点
export function addLinePoint(stageContext, nodeId, segmentIndex, position = null) {
  const node = stageContext.api.getNode(nodeId);
  if (!node || node.type !== TYPES.line) return false;

  const points = deserializePoints(node.points);
  if (!points || points.length < 2) return false;

  if (segmentIndex < 0 || segmentIndex >= points.length - 1) return false;

  const start = points[segmentIndex];
  const end = points[segmentIndex + 1];

  // 如果没有指定位置，使用线段中点
  const newPoint = position || {
    x: (start.x + end.x) / 2,
    y: (start.y + end.y) / 2,
  };

  const newPoints = [...points];
  newPoints.splice(segmentIndex + 1, 0, newPoint);

  // 更新节点
  stageContext.api.updateNode(node.id, {
    points: serializePoints(newPoints),
  });

  stageContext.hooks.onNodeResizeCallback();
  return true;
}
